import { myEnergySelectors } from 'ducks/myEnergy'
import pick from 'lodash/pick'
import objectHash from 'object-hash'
import { FIELDS_REQUIRE_RECALC } from 'projectSections/form/fields'
import { buildChangeOrderProposalCacheSnapshot, ChangeOrderProjectValues } from 'util/changeOrder'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ProposalDataType } from 'types/proposals'
import { StudioSystemType } from 'types/studio/items'

export function buildProposalCacheRelevantSnapshot(
  values: Record<string, any> | null | undefined
): Record<string, any> {
  if (!values) return {}
  return {
    ...pick(values, FIELDS_REQUIRE_RECALC),
    ...buildChangeOrderProposalCacheSnapshot(values as ChangeOrderProjectValues),
  }
}

export const useGetProposalHasCashFlowDeposit = () => {
  const [hasCashFlowDeposit, setHasCashFlowDeposit] = useState(false)

  const availableActions = useSelector(myEnergySelectors.getProposalAvailableActions)

  useEffect(() => {
    if (!availableActions?.length) setHasCashFlowDeposit(false)
    else {
      let foundCashFlow = false
      availableActions.forEach((systemActions) => {
        systemActions?.actions_available?.forEach((action) => {
          if (action.payment_method === 'cashflow' && action.payment_amount) foundCashFlow = true
        })
      })
      setHasCashFlowDeposit(foundCashFlow)
    }
  }, [availableActions])

  return hasCashFlowDeposit
}

export function checkIfCalculationsBlockByShading(system: StudioSystemType, ignoreAwaitingTrigger = false): boolean {
  if (!system) return false

  // If the system already has shadingCalcsAwaitingTrigger set to true this suggests that its left the debounce queue
  // and will complete naturally in the calculation queue, we should still wait for it to finish, but don't need to perform further checks
  if (system.shadingCalcsAwaitingTrigger && !ignoreAwaitingTrigger) {
    return false
  }

  const raytracedShadingAvailable =
    typeof system.raytracedShadingAvailable === 'function' ? system.raytracedShadingAvailable() : false

  const panelsMissingShading = window.ShadeHelper?.panelsMissingShading(system, raytracedShadingAvailable, false) || []
  const maxPanelsShadingTrigger = window.ShadeHelper?.getMaxPanelsShadingTrigger() || 0

  // Do a runtime check to see if the system calcs would end up blocked by the recalculate button
  return panelsMissingShading.length > maxPanelsShadingTrigger && raytracedShadingAvailable
}

function shouldWaitForDebounceQueueCalculations(debounceQueue: string[]): boolean {
  return debounceQueue.length > 0
}

export function shouldWaitForCalculations(calculationStatus: any): boolean {
  if (!calculationStatus) return false

  // Check debounce queue first
  if (shouldWaitForDebounceQueueCalculations(calculationStatus.debounceQueue || [])) {
    return true
  }

  // Check process queue
  const processQueueUuids = Object.keys(calculationStatus.processQueue || {})
  return processQueueUuids.length > 0
}

export const getProposalV2DesignStatus = (proposalData: ProposalDataType) => {
  const MAX_SYSTEMS_THRESHOLD = 6

  const isSystemLimitExceeded = Boolean(proposalData.systems.length > MAX_SYSTEMS_THRESHOLD)
  const isResidential = Boolean(proposalData.selectedProject.proposal_data?.project.is_residential)

  const isValidProjectType = isResidential
  const isValidDesign = !isSystemLimitExceeded && isValidProjectType

  return {
    isSystemLimitExceeded,
    isValidProjectType,
    isValidDesign,
  }
}

function compareState(stateA, stateB) {
  const stateADiffs = {}
  for (const key in stateA) {
    if (stateA[key] !== stateB[key]) {
      stateADiffs[key] = stateA[key]
    }
  }
  const stateBDiffs = {}
  for (const key in stateB) {
    if (stateB[key] !== stateA[key]) {
      stateBDiffs[key] = stateB[key]
    }
  }
  if (Object.values(stateADiffs).length === 0 && Object.values(stateBDiffs).length === 0) {
    return
  }
  return [stateADiffs, stateBDiffs]
}

// The following method is a hack in order to track the sentry error https://github.com/open-solar/opensolar-todo/issues/17714
export const trackUnexpectedComponentDidUpdate = ({
  preProps,
  curProps,
  preState,
  curState,
}: {
  preProps: Object
  curProps: Object
  preState: Object
  curState: Object
}) => {
  const propsDiffs = compareState(preProps, curProps)
  if (preProps !== curProps) {
    console.log('TrackUnexpectedComponentDidUpdate: props updated')
  }
  if (propsDiffs) {
    console.log(`preProps diff keys: ${Object.keys(propsDiffs[0]).join(',')}`)
    console.log(`curProps diff keys: ${Object.keys(propsDiffs[1]).join(',')}`)
  }
  const stateDiffs = compareState(preState, curState)
  if (preState !== curState) {
    console.log('TrackUnexpectedComponentDidUpdate: state updated')
  }
  if (stateDiffs) {
    console.log(`preState diff keys: ${Object.keys(stateDiffs[0]).join(',')}`)
    console.log(`curState diff keys: ${Object.keys(stateDiffs[1]).join(',')}`)
  }
}

const decompressAndParse = (obj, key, parseJson = true) => {
  if (obj[key] && typeof obj[key] === 'string') {
    try {
      obj[key] = window.CompressionHelper.decompress(obj[key], parseJson)
    } catch (e) {
      console.error(`Error decompressing ${key} data:`, e)
    }
  }
}

export const decompressProposalData = (data) => {
  try {
    data[0].projects.forEach(function (p) {
      p.proposal_data.design = window.CompressionHelper.decompress(p.proposal_data.design, true)
      const systems = p.proposal_data.systems
      systems.forEach((s) => {
        decompressAndParse(s.data, 'output')
        decompressAndParse(s.data, 'bills')
        if (s.data && s.data.payment_options) {
          s.data.payment_options.forEach((po) => {
            decompressAndParse(po, 'contract_template', false)
          })
        }
      })
    })
  } catch (err) {
    console.log(err)
  }
  return data
}

export const generateProjectCacheKey = (
  projectId: string | number | null,
  postData: any,
  queryParams: any,
  formFieldsRequiringCalculationsSnapshot: Record<string, any> | null | undefined = undefined
): string => {
  const cacheInput = {
    projectId: projectId || null,
    postData: postData || {},
    queryParams: queryParams || {},
    formFieldsRequiringCalculationsSnapshot: formFieldsRequiringCalculationsSnapshot || {},
  }

  return objectHash(cacheInput, { algorithm: 'md5', encoding: 'hex' })
}

export const buildProjectDataQueryParams = (expoEnabled: boolean, compressBillsAndOutput: boolean) => {
  const queryParams: any = {}
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('include_unsold') === '1' || window.location.href.includes('&url_src=next_steps')) {
    queryParams.include_unsold = '1'
  }
  if (expoEnabled) queryParams.expo_enabled = '1'
  if (compressBillsAndOutput) queryParams.compress_data = '1'
  return queryParams
}

const PROJECTS_WITH_NUMERIC_ID = /^\/projects\/(\d+)(?:\/|$)/

/** Full-page cashflow checkout v2 routes (see routes.jsx). */
export function isCashflowCheckoutRoutePath(pathname: string): boolean {
  return /^\/projects\/\d+\/checkout$/.test(pathname) || pathname === '/myenergy/checkout'
}

/** Proposal share links use `token`; checkout expects `url_auth_token`. */
export function getProposalUrlAuthToken(search: string): string | undefined {
  const params = new URLSearchParams(search)
  return params.get('token') ?? params.get('url_auth_token') ?? undefined
}

export function getProjectCashflowCheckoutPath(
  pathname: string,
  projectId: number,
  systemUuid: string,
  paymentOptionId: number,
  countryIso2?: string,
  urlAuthToken?: string
): string {
  const params = new URLSearchParams()
  params.set('system_uuid', systemUuid)
  params.set('payment_option_id', String(paymentOptionId))
  if (countryIso2) {
    params.set('country_iso2', countryIso2)
  }
  if (urlAuthToken) {
    params.set('url_auth_token', urlAuthToken)
  }
  const qs = params.toString()

  if (PROJECTS_WITH_NUMERIC_ID.test(pathname)) {
    return `/projects/${projectId}/checkout?${qs}`
  }

  const q = new URLSearchParams(params)
  q.set('project_id', String(projectId))
  return `/myenergy/checkout?${q.toString()}`
}

export function getProposalReturnPathFromCheckout(
  pathname: string,
  projectId: number,
  search: string
): string | null {
  if (pathname.includes('/myenergy/')) {
    const urlAuthToken = getProposalUrlAuthToken(search)
    const qs = urlAuthToken ? `?token=${encodeURIComponent(urlAuthToken)}` : ''
    return `/myenergy/${projectId}${qs}`
  }
  if (/^\/projects\/\d+\/checkout$/.test(pathname)) {
    return `/projects/${projectId}/proposal`
  }
  return null
}
